/* iceberg-ui */
import { Box } from '@auviknetworks/iceberg-ui';

/* Shared */
import { TopInfoBanner } from 'application/Shared/Components/TopInfoBanner';
import { Notify } from 'application/Shared/Helpers';
import { ProviderType } from 'application/Shared/types/tenantProvider';
import { usePostClientLink } from 'hooks/dataAccess/ClientLinks/clientLink';

/* Hooks */
import {
    useCreateTenantProviderAsync,
    useFetchTenantProviderAsync
} from 'hooks/dataAccess/Tenant/Provider/tenantProvider';
import { useCreateTenantProviderAuthAsync } from 'hooks/dataAccess/Tenant/ProviderAuth/tenantProviderAuth';
import { usePutTenant } from 'hooks/dataAccess/Tenant/tenant';
import { useUpdateSession } from 'hooks/redux/Selectors/session';
import Session from 'objects/Session';

/* React */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntegrationDescription } from '../Components/IntegrationDescription';
import {
    INTEGRATION_OPTION_GOOGLE_AUTOMATED,
    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED,
    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_MANUAL,
    IntegrationSelector
} from '../Components/IntegrationSelector';
import { GoogleAutomatedIntegrationForm } from '../Forms/GoogleAutomatedIntegrationForm';
import { MicrosoftAutomatedIntegrationForm } from '../Forms/MicrosoftAutomatedIntegrationForm';
import { MicrosoftManualIntegrationForm } from '../Forms/MicrosoftManualIntegrationForm';
import { Step } from '../Forms/components/Step';
import {
    makePayload,
    makeProviderAuthData,
    makeUpdateTenantPayload
} from '../Forms/functions';
import { WizardContext } from '../Wizard';

export const ConfigureIntegration = ({
    clientId,
    filterExistingSyncedTenants,
    formFooterComponent,
    initialStep = 1,
    integrationType,
    integrationAuthenticated,
    onboarding,
    onSuccess,
    setIntegrationType,
    setIsSubmitting,
    setLinkedTenantId,
    showGroups,
    tenantSyncConfig,
    tenantToEditIntegration,
    useIntegrationWizard = true
}) => {
    const { t } = useTranslation();
    const { toNextStep } = useContext(WizardContext);
    const updateSession = useUpdateSession();

    const microsoftEntraIDIntegrationOptions = [
        INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED,
        INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_MANUAL
    ];

    const { getProviderConfig } = useFetchTenantProviderAsync();
    const { createTenantProvider, saving: isSavingTenantProvider } =
        useCreateTenantProviderAsync();
    const { createTenantProviderAuth, saving: isSavingTenantProviderAuth } =
        useCreateTenantProviderAuthAsync();
    const { putTenant, saving: isSavingTenant } = usePutTenant();
    const {
        postClientLink,
        saving: isPostingClientLink,
        linkedTenantId
    } = usePostClientLink();

    const [isSuccess, setIsSuccess] = useState(false);
    const [providerConfig, setProviderConfig] = useState({
        ...tenantSyncConfig
    });

    const createClient = async (values) => {
        const payload = makePayload(values);

        const response = await postClientLink(payload);
        if (!response) {
            Notify.e(t('Provider.Shared.unable_create_client'));
            return;
        }

        // reload the session to update partnerTenantLinks so they will be shown in TenantSelector in the sidebar
        const userSession = await Session.getSessionInfo();
        updateSession(userSession);

        Notify.s(t('Provider.Shared.created_client'));
    };

    const onSubmit = (values, providerType) => {
        if (clientId) {
            upsertTenantProviderFn(values, providerType);
        } else {
            createClient(values);
        }
    };

    const upsertTenantProvider = async (values, enabled) => {
        const providerPayload = {
            enabled,
            identityTenantId: values.azureTenantId,
            platformTenantId: clientId,
            providerType: values.providerType
        };
        return createTenantProvider(providerPayload);
    };

    const upsertTenantProviderAuth = async (values, providerType) => {
        const authData = makeProviderAuthData(values, providerType);

        const providerAuthPayload = {
            authData,
            identityTenantId: values.azureTenantId,
            platformTenantId: clientId,
            providerType,
            upsert: true
        };

        return createTenantProviderAuth(providerAuthPayload);
    };

    const upsertTenantProviderFn = async (values, providerType) => {
        const { error: providerError } = await upsertTenantProvider(
            values,
            true
        );
        if (providerError) {
            Notify.e(t('Provider.Shared.error_saving_settings'));
            return;
        }
        const { error: providerAuthError } = await upsertTenantProviderAuth(
            values,
            providerType
        );
        if (providerAuthError) {
            Notify.e(t('Provider.Shared.error_saving_settings'));
            await upsertTenantProvider(values, false);
            return;
        }
        const updateTenantPayload = makeUpdateTenantPayload(values);
        if (updateTenantPayload) {
            const updated = await putTenant({
                id: clientId,
                params: updateTenantPayload
            });
            if (!updated) {
                Notify.e(t('Provider.Shared.error_saving_settings'));
                return;
            }
        }
        Notify.s(t(`Provider.${providerType}Configuration.enabled_and_saved`));
        setIsSuccess(true);
    };

    useEffect(() => {
        setIsSubmitting(
            isPostingClientLink ||
                isSavingTenantProvider ||
                isSavingTenantProviderAuth ||
                isSavingTenant
        );
    }, [
        isPostingClientLink,
        isSavingTenantProvider,
        isSavingTenantProviderAuth,
        isSavingTenant,
        setIsSubmitting
    ]);

    useEffect(() => {
        if (linkedTenantId) {
            setLinkedTenantId(linkedTenantId);
            setIsSuccess(true);
        }
    }, [linkedTenantId, setLinkedTenantId, setIsSuccess]);

    useEffect(() => {
        if (isSuccess) {
            if (toNextStep) {
                toNextStep();
            } else if (onSuccess) {
                onSuccess(linkedTenantId);
            }
        }
    }, [isSuccess, toNextStep, onSuccess, linkedTenantId]);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getProviderConfig(tenantToEditIntegration?.id);
            setProviderConfig(data.providerConfig);
        };

        // We have a tenant to update, but we don't have the provider info
        if (!tenantSyncConfig && tenantToEditIntegration?.id) {
            fetchData();
        }
    }, [getProviderConfig, tenantSyncConfig, tenantToEditIntegration?.id]);

    // Whenever this component unmounts, clear any Google Workspace or Microsoft Entra ID keys from localStorage.
    useEffect(
        () => () => {
            Object.keys(localStorage)
                .filter(
                    (key) =>
                        key.startsWith('gws') ||
                        key.startsWith('adminAccessGranted-') ||
                        key.startsWith('accessToken')
                )
                .forEach((key) => localStorage.removeItem(key));
        },
        []
    );

    return (
        <div style={{ height: '100%' }}>
            {microsoftEntraIDIntegrationOptions.includes(integrationType) &&
                !useIntegrationWizard && (
                    <Box mx="24px">
                        <TopInfoBanner
                            align="left"
                            backgroundColor="topInfoBanner.configureIntegration.backgroundColor"
                            borderColor="topInfoBanner.configureIntegration.borderColor"
                            displayIcon={false}
                            textColor="topInfoBanner.configureIntegration.textColor"
                            title={t('Common.important_note')}
                            titleColor="topInfoBanner.configureIntegration.titleColor"
                            infoText={t(
                                'Provider.MicrosoftConfiguration.integration_message'
                            )}
                        />
                    </Box>
                )}
            <Box display="flex" flexDirection="column" height="100%">
                {microsoftEntraIDIntegrationOptions.includes(
                    integrationType
                ) && (
                    <Box mt={4}>
                        <Step
                            number={initialStep}
                            details={t(
                                'Clients.addModal.steps.1.microsoftEntraID'
                            )}
                        />
                        <IntegrationSelector
                            selectedIntegrationType={integrationType}
                            setIntegrationType={setIntegrationType}
                            integrationOptions={
                                microsoftEntraIDIntegrationOptions
                            }
                        />
                    </Box>
                )}
                {integrationType ===
                    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED && (
                    <>
                        <Box mt={2}>
                            <Step
                                number={initialStep + 1}
                                details={t(
                                    'Clients.addModal.steps.2.microsoftEntraIDAutomated'
                                )}
                            />
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            height="100%"
                            justifyContent="space-between"
                        >
                            <IntegrationDescription />
                            <MicrosoftAutomatedIntegrationForm
                                clientId={clientId}
                                filterExistingSyncedTenants={
                                    filterExistingSyncedTenants
                                }
                                initialStep={initialStep + 2}
                                onSubmit={(values) =>
                                    onSubmit(values, ProviderType.Microsoft)
                                }
                                onboarding={!!onboarding}
                                showGroups={showGroups}
                                tenantSyncConfigId={providerConfig?.id}
                                tenantToEditIntegration={
                                    tenantToEditIntegration
                                }
                                useIntegrationWizard={useIntegrationWizard}
                            >
                                {formFooterComponent}
                            </MicrosoftAutomatedIntegrationForm>
                        </Box>
                    </>
                )}
                {integrationType ===
                    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_MANUAL && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        height="100%"
                        justifyContent="space-between"
                    >
                        <MicrosoftManualIntegrationForm
                            clientId={clientId}
                            onboarding={!!onboarding}
                            onSubmit={(values) =>
                                onSubmit(values, ProviderType.Microsoft)
                            }
                            tenantSyncConfigId={providerConfig?.id}
                            tenantToEditIntegration={tenantToEditIntegration}
                            useIntegrationWizard={useIntegrationWizard}
                        >
                            {formFooterComponent}
                        </MicrosoftManualIntegrationForm>
                    </Box>
                )}
                {integrationType === INTEGRATION_OPTION_GOOGLE_AUTOMATED && (
                    <>
                        <Box mt={4}>
                            <Step
                                number={initialStep}
                                details={t(
                                    'Clients.addModal.steps.2.googleAutomated'
                                )}
                            />
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            height="100%"
                            justifyContent="space-between"
                        >
                            <GoogleAutomatedIntegrationForm
                                clientId={clientId}
                                onboarding={!!onboarding}
                                onSubmit={(values) =>
                                    onSubmit(values, ProviderType.Google)
                                }
                                tenantToEditIntegration={
                                    tenantToEditIntegration
                                }
                                useIntegrationWizard={useIntegrationWizard}
                                integrationAuthenticated={
                                    integrationAuthenticated
                                }
                            >
                                {formFooterComponent}
                            </GoogleAutomatedIntegrationForm>
                        </Box>
                    </>
                )}
            </Box>
        </div>
    );
};
